import { useEffect, useState } from "react";
import { adminListDonations, fetchAdminSettings, adminUpdateSettings } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Plus, Trash2, Save, Loader2, Users } from "lucide-react";
import { toast } from "sonner";

export default function AdminDonations() {
  const { reload } = useSettings();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wall, setWall] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminListDonations().then(setItems).finally(() => setLoading(false));
    fetchAdminSettings().then((d) => setWall(d.donor_wall || [])).catch(() => setWall([]));
  }, []);

  const paid = items.filter((d) => d.status === "paid" || d.status === "active");
  const total = paid.reduce((s, d) => s + (d.amount || 0), 0);

  const setRow = (i, k, v) => setWall((p) => p.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const addRow = () => setWall((p) => [...(p || []), { name: "", amount: 1100, note: "" }]);
  const delRow = (i) => setWall((p) => p.filter((_, idx) => idx !== i));

  const saveWall = async () => {
    setSaving(true);
    try {
      const cleaned = (wall || [])
        .filter((r) => r.name && r.name.trim())
        .map((r) => ({ name: r.name.trim(), amount: Math.max(1, Number(r.amount) || 0), note: (r.note || "").trim() }));
      await adminUpdateSettings({ donor_wall: cleaned });
      await reload();
      setWall(cleaned);
      toast.success("Supporter wall saved");
    } catch (e) {
      toast.error("Could not save", { description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  return (
    <div>
      <h1 className="mb-1 font-serif-latin text-3xl text-ink">Donations</h1>
      <p className="mb-6 text-sm text-ink-3">{loading ? "…" : `${paid.length} successful · ₹${total} received`}</p>

      {/* Donor wall editor */}
      <div className="mb-8 border border-line bg-surface p-5" data-testid="donor-wall-editor">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 font-serif-latin text-xl text-ink"><Users className="h-5 w-5 text-saffron" /> Featured Supporters (donor wall)</h2>
            <p className="mt-1 text-xs text-ink-3">Shown publicly on the Support page alongside real verified donations. Real donors appear automatically; use this to feature names for social proof.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={addRow} data-testid="donor-add" className="inline-flex items-center gap-1.5 border border-line px-3 py-1.5 text-sm text-ink-2 transition-colors hover:border-[var(--ink)] hover:text-ink"><Plus className="h-4 w-4" /> Add</button>
            <button onClick={saveWall} disabled={saving} data-testid="donor-wall-save" className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-4 py-1.5 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
            </button>
          </div>
        </div>
        {wall === null ? (
          <p className="py-6 text-sm text-ink-3">Loading…</p>
        ) : (
          <div className="mt-4 space-y-2">
            {wall.length === 0 && <p className="text-sm text-ink-3">No featured supporters. Click “Add” to create one.</p>}
            {wall.map((r, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`donor-row-${i}`}>
                <input value={r.name || ""} onChange={(e) => setRow(i, "name", e.target.value)} placeholder="Name" data-testid={`donor-name-${i}`} className="min-w-[10rem] flex-1 border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
                <input value={r.note || ""} onChange={(e) => setRow(i, "note", e.target.value)} placeholder="City / note (optional)" className="min-w-[8rem] flex-1 border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
                <input type="number" min="1" value={r.amount ?? ""} onChange={(e) => setRow(i, "amount", e.target.value)} placeholder="₹" data-testid={`donor-amount-${i}`} className="w-28 border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none dh-num" />
                <button onClick={() => delRow(i)} data-testid={`donor-del-${i}`} className="p-2 text-ink-3 transition-colors hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {loading ? <p className="py-16 text-center text-ink-3">…</p> : (
        <div className="overflow-hidden border border-line bg-surface">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-line bg-surface-alt/50 text-xs uppercase tracking-wider text-ink-3">
              <tr><th className="px-4 py-3">Ref</th><th className="px-4 py-3">Donor</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Status</th><th className="hidden px-4 py-3 sm:table-cell">Date</th></tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.id} data-testid={`donation-${d.ref}`} className="border-b border-line last:border-0">
                  <td className="px-4 py-3 font-mono text-xs text-ink">{d.ref}</td>
                  <td className="px-4 py-3 text-ink-2">{d.name || "—"}<span className="block text-xs text-ink-3">{d.email}</span></td>
                  <td className="px-4 py-3 text-ink">₹{d.amount}</td>
                  <td className="px-4 py-3 text-ink-2">{d.type === "subscription" ? "Monthly" : "One-time"}</td>
                  <td className="px-4 py-3"><span className={(d.status === "paid" || d.status === "active") ? "text-saffron" : "text-ink-3"}>{d.status}</span></td>
                  <td className="hidden px-4 py-3 text-ink-3 sm:table-cell">{d.created_at?.slice(0, 10)}</td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={6} className="px-4 py-16 text-center text-ink-3">No donations yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
